import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import emailjs from "@emailjs/browser";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// EmailJS configuration from environment variables
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_AUTOREPLY_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_AUTOREPLY_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

// Initialize EmailJS
emailjs.init(EMAILJS_PUBLIC_KEY);

const buildingTypes = [
  { value: "valyoghaz", label: "Vályogház" },
  { value: "vegyes", label: "Vegyes falazatú ház" },
  { value: "tegla", label: "Téglaépület" },
];

const constructionYears = [
  { value: "1950_elott", label: "1950 előtt" },
  { value: "1950_1970", label: "1950–1970" },
  { value: "1970_utan", label: "1970 után" },
  { value: "nem_tudom", label: "Nem tudom" },
];

const timelines = [
  { value: "azonnal", label: "Azonnal, vagy 1–2 héten belül" },
  { value: "2_4_het", label: "2–4 héten belül" },
  { value: "1_2_honap", label: "1–2 hónapon belül" },
];

const buildingSizes = [
  { value: "kicsi", label: "Kicsi", sublabel: "50 m² alatt", icon: "S" },
  { value: "kozepes", label: "Közepes", sublabel: "51–100 m²", icon: "M" },
  { value: "nagy", label: "Nagy", sublabel: "101–200 m²", icon: "L" },
  { value: "hatalmas", label: "Hatalmas", sublabel: "201 m² felett", icon: "XL" },
];

const initialFormData = {
  building_type: "",
  construction_year: "",
  project_description: "",
  timeline: "",
  building_size: "",
  last_name: "",
  first_name: "",
  email: "",
  phone: "",
  postal_code: "",
  city: "",
  address: "",
};

const SurveyForm = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const totalSteps = 6;

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.building_type) {
          toast.error("Kérjük, válassza ki az épület típusát!");
          return false;
        }
        return true;
      case 2:
        if (!formData.construction_year) {
          toast.error("Kérjük, válassza ki az építés évét!");
          return false;
        }
        return true;
      case 3:
        if (!formData.project_description.trim()) {
          toast.error("Kérjük, írja le röviden a projektet!");
          return false;
        }
        if (!formData.timeline) {
          toast.error("Kérjük, válassza ki a tervezett időpontot!");
          return false;
        }
        return true;
      case 4:
        if (!formData.building_size) {
          toast.error("Kérjük, válassza ki az épület méretét!");
          return false;
        }
        return true;
      case 5:
        if (!formData.last_name.trim() || !formData.first_name.trim()) {
          toast.error("Kérjük, adja meg a nevét!");
          return false;
        }
        if (!formData.email.trim() || !formData.phone.trim()) {
          toast.error("Kérjük, adja meg az elérhetőségeit!");
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          toast.error("Kérjük, adjon meg érvényes email címet!");
          return false;
        }
        return true;
      case 6:
        if (!formData.postal_code.trim() || !formData.city.trim() || !formData.address.trim()) {
          toast.error("Kérjük, adja meg a munka helyszínét!");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  // Helper function to get readable labels
  const getLabel = (value, options) => {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setIsSubmitting(true);
    try {
      // Save to database
      await axios.post(`${API}/survey`, formData);
      
      // Send email via EmailJS
      const emailParams = {
        building_type: getLabel(formData.building_type, buildingTypes),
        construction_year: getLabel(formData.construction_year, constructionYears),
        project_description: formData.project_description,
        timeline: getLabel(formData.timeline, timelines),
        building_size: getLabel(formData.building_size, buildingSizes),
        last_name: formData.last_name,
        first_name: formData.first_name,
        email: formData.email,
        phone: formData.phone,
        postal_code: formData.postal_code,
        city: formData.city,
        address: formData.address,
        to_email: formData.email,
        from_name: `${formData.last_name} ${formData.first_name}`,
      };
      
      // Send notification to owner
      try {
        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, emailParams);
        console.log("Owner notification sent");
      } catch (emailError) {
        console.error("Owner email error:", emailError);
      }
      
      // Send auto-reply to customer - needs to_email for the recipient
      const autoReplyParams = {
        ...emailParams,
        to_email: formData.email,
        to_name: `${formData.first_name} ${formData.last_name}`,
        user_email: formData.email,
        user_name: `${formData.first_name} ${formData.last_name}`,
        reply_to: formData.email,
        name: `${formData.first_name} ${formData.last_name}`,
      };
      
      try {
        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_AUTOREPLY_TEMPLATE_ID, autoReplyParams);
        console.log("Auto-reply sent");
      } catch (autoReplyError) {
        console.error("Auto-reply error:", autoReplyError);
      }
      
      setIsSubmitted(true);
      toast.success("Köszönjük! Hamarosan felvesszük Önnel a kapcsolatot.");
    } catch (error) {
      console.error("Survey submission error:", error);
      toast.error("Hiba történt a küldés során. Kérjük, próbálja újra!");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div
        className="bg-white p-12 rounded-3xl shadow-card text-center overflow-hidden relative"
        data-testid="survey-success"
      >
        {/* Animated background circles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-blue-100 rounded-full animate-pulse opacity-60" />
          <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-yellow-100 rounded-full animate-pulse opacity-60" style={{ animationDelay: '0.5s' }} />
          <div className="absolute top-1/2 -left-5 w-20 h-20 bg-green-100 rounded-full animate-pulse opacity-40" style={{ animationDelay: '1s' }} />
        </div>

        {/* Confetti animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-3 h-3 rounded-full"
              style={{
                background: ['#1E3A8A', '#FBBF24', '#10B981', '#EF4444', '#8B5CF6'][i % 5],
                left: `${10 + (i * 7)}%`,
                top: '-10px',
                animation: `confetti-fall 3s ease-out ${i * 0.1}s forwards`
              }}
            />
          ))}
        </div>

        {/* Main content with bounce animation */}
        <div className="relative z-10" style={{ animation: 'bounce-in 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards' }}>
          {/* Success icon with pop animation */}
          <div 
            className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full mx-auto mb-6 flex items-center justify-center shadow-lg"
            style={{ animation: 'pop-in 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.2s forwards', opacity: 0, transform: 'scale(0)' }}
          >
            <Check className="w-12 h-12 text-white" strokeWidth={3} />
          </div>

          {/* Celebration emoji row */}
          <div 
            className="flex justify-center gap-4 mb-6"
            style={{ animation: 'slide-up 0.5s ease-out 0.4s forwards', opacity: 0, transform: 'translateY(20px)' }}
          >
            <span className="text-4xl" style={{ animation: 'wiggle 1s ease-in-out infinite' }}>🎉</span>
            <span className="text-4xl" style={{ animation: 'wiggle 1s ease-in-out 0.2s infinite' }}>🏠</span>
            <span className="text-4xl" style={{ animation: 'wiggle 1s ease-in-out 0.4s infinite' }}>🎉</span>
          </div>

          {/* Title with slide animation */}
          <h3
            className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4"
            style={{ 
              fontFamily: "'Manrope', sans-serif",
              animation: 'slide-up 0.5s ease-out 0.3s forwards',
              opacity: 0,
              transform: 'translateY(20px)'
            }}
          >
            Köszönjük érdeklődését!
          </h3>

          {/* Subtitle */}
          <p 
            className="text-slate-600 text-lg mb-8 max-w-md mx-auto"
            style={{ animation: 'slide-up 0.5s ease-out 0.5s forwards', opacity: 0, transform: 'translateY(20px)' }}
          >
            Kérdőívét megkaptuk és feldolgoztuk. Szakértőink <strong className="text-blue-900">24 órán belül</strong> felveszik Önnel a kapcsolatot!
          </p>

          {/* Contact card */}
          <div 
            className="bg-blue-50 rounded-2xl p-6 max-w-sm mx-auto"
            style={{ animation: 'slide-up 0.5s ease-out 0.7s forwards', opacity: 0, transform: 'translateY(20px)' }}
          >
            <p className="text-blue-900 font-semibold mb-2">Sürgős esetben hívjon minket:</p>
            <a 
              href="tel:+36309669528" 
              className="text-2xl font-bold text-blue-900 hover:text-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              +36 30 966 9528
            </a>
          </div>
        </div>

        {/* CSS Animations */}
        <style>{`
          @keyframes bounce-in {
            0% { opacity: 0; transform: scale(0.3) translateY(50px); }
            50% { transform: scale(1.05) translateY(-10px); }
            70% { transform: scale(0.95) translateY(5px); }
            100% { opacity: 1; transform: scale(1) translateY(0); }
          }
          
          @keyframes pop-in {
            0% { opacity: 0; transform: scale(0) rotate(-180deg); }
            60% { transform: scale(1.2) rotate(10deg); }
            100% { opacity: 1; transform: scale(1) rotate(0deg); }
          }
          
          @keyframes slide-up {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes wiggle {
            0%, 100% { transform: rotate(-5deg); }
            50% { transform: rotate(5deg); }
          }
          
          @keyframes confetti-fall {
            0% { transform: translateY(0) rotate(0deg); opacity: 1; }
            100% { transform: translateY(400px) rotate(720deg); opacity: 0; }
          }
        `}</style>
      </div>
    );
  }

  const OptionCard = ({ selected, onClick, children, testId }) => (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`w-full p-5 text-left rounded-2xl border-2 transition-all ${
        selected
          ? "border-blue-900 bg-blue-50"
          : "border-slate-100 hover:border-blue-200 hover:bg-blue-50/30"
      }`}
    >
      {children}
    </button>
  );

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3
              className="text-2xl font-bold text-slate-900 mb-6"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Milyen típusú épülettel rendelkezik?
            </h3>
            <div className="grid gap-3">
              {buildingTypes.map((type) => (
                <OptionCard
                  key={type.value}
                  testId={`building-type-${type.value}`}
                  selected={formData.building_type === type.value}
                  onClick={() => updateField("building_type", type.value)}
                >
                  <span className="text-lg font-semibold text-slate-900">{type.label}</span>
                </OptionCard>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3
              className="text-2xl font-bold text-slate-900 mb-6"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Mikor épült az épület?
            </h3>
            <div className="grid gap-3">
              {constructionYears.map((year) => (
                <OptionCard
                  key={year.value}
                  testId={`construction-year-${year.value}`}
                  selected={formData.construction_year === year.value}
                  onClick={() => updateField("construction_year", year.value)}
                >
                  <span className="text-lg font-semibold text-slate-900">{year.label}</span>
                </OptionCard>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3
                className="text-2xl font-bold text-slate-900 mb-6"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Írja le röviden a projektet
              </h3>
              <textarea
                data-testid="project-description"
                value={formData.project_description}
                onChange={(e) => updateField("project_description", e.target.value)}
                placeholder="Milyen munkálatokat szeretne elvégeztetni?"
                className="w-full h-32 p-4 rounded-2xl border-2 border-slate-100 focus:border-blue-900 transition-colors resize-none text-lg outline-none"
              />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-slate-900 mb-4">
                Mikor tervezi a munka kezdetét?
              </h4>
              <div className="grid gap-3">
                {timelines.map((tl) => (
                  <OptionCard
                    key={tl.value}
                    testId={`timeline-${tl.value}`}
                    selected={formData.timeline === tl.value}
                    onClick={() => updateField("timeline", tl.value)}
                  >
                    <span className="font-medium text-slate-900">{tl.label}</span>
                  </OptionCard>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3
              className="text-2xl font-bold text-slate-900 mb-6"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Mekkora az épület alapterülete?
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {buildingSizes.map((size) => (
                <OptionCard
                  key={size.value}
                  testId={`building-size-${size.value}`}
                  selected={formData.building_size === size.value}
                  onClick={() => updateField("building_size", size.value)}
                >
                  <div className="text-center py-2">
                    <div className="text-3xl font-extrabold text-blue-900 mb-1">{size.icon}</div>
                    <div className="font-semibold text-slate-900">{size.label}</div>
                    <div className="text-sm text-slate-500">{size.sublabel}</div>
                  </div>
                </OptionCard>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-5">
            <h3
              className="text-2xl font-bold text-slate-900 mb-6"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Személyes adatok
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">Vezetéknév *</label>
                <input
                  type="text"
                  data-testid="last-name"
                  value={formData.last_name}
                  onChange={(e) => updateField("last_name", e.target.value)}
                  className="w-full p-4 rounded-2xl border-2 border-slate-100 focus:border-blue-900 transition-colors text-lg outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">Keresztnév *</label>
                <input
                  type="text"
                  data-testid="first-name"
                  value={formData.first_name}
                  onChange={(e) => updateField("first_name", e.target.value)}
                  className="w-full p-4 rounded-2xl border-2 border-slate-100 focus:border-blue-900 transition-colors text-lg outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">Email cím *</label>
              <input
                type="email"
                data-testid="email"
                value={formData.email}
                onChange={(e) => updateField("email", e.target.value)}
                className="w-full p-4 rounded-2xl border-2 border-slate-100 focus:border-blue-900 transition-colors text-lg outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">Telefonszám *</label>
              <input
                type="tel"
                data-testid="phone"
                value={formData.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                className="w-full p-4 rounded-2xl border-2 border-slate-100 focus:border-blue-900 transition-colors text-lg outline-none"
              />
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-5">
            <h3
              className="text-2xl font-bold text-slate-900 mb-6"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              A munka helyszíne
            </h3>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">Irányítószám *</label>
              <input
                type="text"
                data-testid="postal-code"
                value={formData.postal_code}
                onChange={(e) => updateField("postal_code", e.target.value)}
                className="w-full p-4 rounded-2xl border-2 border-slate-100 focus:border-blue-900 transition-colors text-lg outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">Település *</label>
              <input
                type="text"
                data-testid="city"
                value={formData.city}
                onChange={(e) => updateField("city", e.target.value)}
                className="w-full p-4 rounded-2xl border-2 border-slate-100 focus:border-blue-900 transition-colors text-lg outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">Utca / Házszám *</label>
              <input
                type="text"
                data-testid="address"
                value={formData.address}
                onChange={(e) => updateField("address", e.target.value)}
                className="w-full p-4 rounded-2xl border-2 border-slate-100 focus:border-blue-900 transition-colors text-lg outline-none"
              />
            </div>
            <p className="text-sm text-slate-500 italic">
              Mielőtt elkezdenénk, természetesen egyeztetünk Önnel.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="bg-white p-8 md:p-12 rounded-3xl shadow-card"
      data-testid="survey-form"
    >
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-slate-500 mb-3">
          <span className="font-medium">Lépés {step} / {totalSteps}</span>
          <span>{Math.round((step / totalSteps) * 100)}%</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-900 rounded-full transition-all duration-300"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Content */}
      <div className="min-h-[300px]">
        {renderStep()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-10 pt-6 border-t border-slate-100">
        <button
          data-testid="survey-prev-btn"
          onClick={prevStep}
          disabled={step === 1}
          className={`flex items-center gap-2 px-6 py-3 font-semibold rounded-full transition-all ${
            step === 1
              ? "text-slate-300 cursor-not-allowed"
              : "text-slate-600 hover:text-blue-900 hover:bg-blue-50"
          }`}
        >
          <ChevronLeft className="w-5 h-5" />
          Vissza
        </button>

        {step < totalSteps ? (
          <button
            data-testid="survey-next-btn"
            onClick={nextStep}
            className="flex items-center gap-2 bg-blue-900 text-white px-8 py-3 font-bold rounded-full hover:bg-blue-800 transition-all shadow-button hover:scale-105 active:scale-95"
          >
            Tovább
            <ChevronRight className="w-5 h-5" />
          </button>
        ) : (
          <button
            data-testid="survey-submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-2 bg-blue-900 text-white px-8 py-3 font-bold rounded-full hover:bg-blue-800 transition-all shadow-button hover:scale-105 active:scale-95 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Küldés...
              </>
            ) : (
              "Kérdőív beküldése"
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default SurveyForm;
